import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getSelectedAssessmentPeriodId } from 'common/selectors/auth';
import userContextSlice from 'common/slices/userContextSlice';
import {
  useGetCultureHubAnalyticsQuery,
  useGetCultureHubPillarsSummaryQuery,
} from '@/areas/culture-hub/api/cultureHubApi';
import type { PillarData, Recommendation } from '@/areas/culture-hub/types/api';

export interface WelcomeTourStrengthItem {
  pillar_name: string;
  category_name: string;
  option_name: string;
  ai_summary?: string;
  overall_score: number;
}

export function useWelcomeTourData() {
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId) as number | null;
  const selectedTeamId = useSelector(
    userContextSlice.selectors.getSelectedTeamId as any
  ) as number | undefined;
  const teamId = selectedTeamId;
  const shouldFetchData = assessmentPeriodId != null && selectedTeamId != null;

  const { data: analytics, isLoading: analyticsLoading, isFetching: analyticsFetching } = useGetCultureHubAnalyticsQuery(
    { assessmentPeriodId: assessmentPeriodId!, teamId },
    { skip: !shouldFetchData }
  );

  const { data: pillarsSummaryData, isLoading: pillarsSummaryLoading, isFetching: pillarsSummaryFetching } = useGetCultureHubPillarsSummaryQuery(
    { assessmentPeriodId: assessmentPeriodId!, teamId },
    { skip: !shouldFetchData }
  );

  const pillars = useMemo<PillarData[]>(
    () => Object.values(pillarsSummaryData?.pillars ?? {}),
    [pillarsSummaryData]
  );

  const focusRecommendations = useMemo<Recommendation[]>(
    () => pillarsSummaryData?.focus_recommendations ?? [],
    [pillarsSummaryData]
  );

  const overallScore = useMemo<number | null>(() => {
    const score = (analytics?.pillar_scores as any)?.overall_score;
    return typeof score === 'number' ? score : null;
  }, [analytics]);

  const biggestStrength = useMemo<WelcomeTourStrengthItem | null>(
    () => ((analytics?.greatest_strengths as any[])?.[0] as WelcomeTourStrengthItem) ?? null,
    [analytics]
  );

  const biggestOpportunity = useMemo<WelcomeTourStrengthItem | null>(
    () => ((analytics?.greatest_deficits as any[])?.[0] as WelcomeTourStrengthItem) ?? null,
    [analytics]
  );

  const aiSummary = useMemo<string | null>(() => {
    const summary = (analytics?.ai_summary as any)?.summary;
    return typeof summary === 'string' ? summary : null;
  }, [analytics]);

  const previousScore = useMemo<number | null>(() => {
    const historicalMetrics = analytics?.historical_performance_metrics as any[] | undefined;
    if (!Array.isArray(historicalMetrics) || historicalMetrics.length < 2) {
      return null;
    }

    const previousPeriod = historicalMetrics[historicalMetrics.length - 2];
    const previousOverall = previousPeriod?.overall_score;
    return typeof previousOverall === 'number' ? previousOverall : null;
  }, [analytics]);

  const differential = useMemo<number | null>(() => {
    if (overallScore === null || previousScore === null) {
      return null;
    }
    return Number((overallScore - previousScore).toFixed(1));
  }, [overallScore, previousScore]);

  // Sorted pillar list: highest to lowest score
  const sortedPillarsByScore = useMemo(
    () => [...pillars].sort((a, b) => b.score - a.score),
    [pillars]
  );

  return {
    analytics,
    pillars,
    focusRecommendations,
    sortedPillarsByScore,
    overallScore,
    previousScore,
    differential,
    biggestStrength,
    biggestOpportunity,
    aiSummary,
    assessmentPeriodId,
    teamId,
    isLoading: analyticsLoading || pillarsSummaryLoading || analyticsFetching || pillarsSummaryFetching,
    hasData: !analyticsLoading && !pillarsSummaryLoading && !!analytics,
  };
}
